/**
 * Shared "does the focused element already own this key?" guards for the
 * window-level keyboard shortcuts (preview paging, media play/pause, selection
 * shortcuts).
 *
 * Every one of those shortcuts calls `preventDefault()`, so each has to let the
 * focused control keep the keys that belong to it. Otherwise the shortcut
 * silently breaks typing, seeking, or a button press.
 */

/**
 * `<input>` types that type nothing, so the keys are not theirs: a focused
 * checkbox, radio, range, file or button input must not silence a shortcut.
 */
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/**
 * True for a focused text field, where every key belongs to the typing. Rich
 * editors are matched by the attribute as well as by `isContentEditable`, which
 * jsdom does not implement.
 */
export const isTextEntryTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    (el.tagName === "INPUT" &&
      !NON_TEXT_INPUT_TYPES.has((el as HTMLInputElement).type)) ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable === true ||
    !!el.closest?.('[contenteditable]:not([contenteditable="false"])')
  );
};

/** True for a focused native media element: Space toggles and the arrows seek. */
export const isMediaElementTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  return el?.tagName === "VIDEO" || el?.tagName === "AUDIO";
};

/**
 * True when the target handles the pressed key itself: a focused media player
 * (Space toggles playback, arrows seek) or a focused text field. A window-level
 * shortcut must leave such an event alone.
 */
export const handlesOwnKeys = (target: EventTarget | null): boolean =>
  isMediaElementTarget(target) || isTextEntryTarget(target);

/**
 * True for a focused control that Space activates — a button, a link, a
 * `role="button"` element, a `<select>` or a `<summary>`. Space's default action
 * *is* the activation there, so a Space shortcut that prevents it would swallow
 * the press (e.g. the preview overlay's close and prev/next buttons).
 */
export const isSpaceActivatedTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  return !!el?.closest?.(
    'button, a[href], [role="button"], [role="link"], select, summary'
  );
};
